import { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Utils } from 'utils';
import Store from 'store';
import { useAuth } from '../../contexts/Auth.contexts';
import Axios from 'axios';
import mqtt from 'mqtt/dist/mqtt';
//import LogoImg from './images/isa2.png';
import LogoImg from './images/isa.jpeg';
import LogoImgSad from './images/isa_sad2.png';
import useHandleSelectOption from '../../components/chatbot/HandleSelectOption';
import useSetupSessionInterface from '../../components/chatbot/SetupSessionInterface';

import {
    defaultOptions,
    defaultOptionsNew,
    optionsSubInformacoesGerais,
    optionsManuaisTutoriais,
    statusList,
    finalizar,
    finalizarIsa,
    atendimentoOptions,
    finalizarAtendimento,
    optionsEmprestimo,
    optionsComunicacaoInternaAreaAtestados,
    optionsComunicacaoInternaAreaDocumentos,
    optionsComunicacaoInternaAreaValeAlimentacao,
    optionsComunicacaoInternaAreaValeTransporte,
    optionsComunicacaoInternaAreaVRAuto,
    optionsComunicacaoInternaAreaVR,
    ausenciaDefault,
} from './chatbotOptions';

// Sub-hooks
import useChatSessions from './hooks/useChatSessions';
import useOnlineStatus from './hooks/useOnlineStatus';
import useChatApi from './hooks/useChatApi';
import useAusenciaApi from './hooks/useAusenciaApi';
import useLoanModal from './hooks/useLoanModal';
import useMessageHandlers from './hooks/useMessageHandlers';

const useChatbotWeb = () => {
    // ─── Shared UI state ──────────────────────────────────────────────────────

    const [selectedSessionId, setSelectedSessionId] = useState(null);
    const [selectedSession, setSelectedSession] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [showOptions, setShowOptions] = useState(false);
    const [showInputBar, setShowInputBar] = useState(false);
    const [remoteTypingStatus, setRemoteTypingStatus] = useState(false);
    const [newTipoChat, setNewTipoChat] = useState('');
    const [status, setStatus] = useState('');
    const [lastTypeChat, setLastTypeChat] = useState(false);
    const [lastAreaChat, setLastAreaChat] = useState(false);
    const [options, setOptions] = useState(defaultOptions);
    const [isBotTyping, setIsBotTyping] = useState(false);
    const [loadingPage, setLoadingPage] = useState(false);
    const [transitionIn, setTransitionIn] = useState(true);
    const [tokenChat, setTokenChat] = useState('');
    const [chatProfile, setChatProfile] = useState({});
    const [chatResponsavel, setChatResponsavel] = useState([]);
    const [calcularSeguro, setCalcularSeguro] = useState('');

    // Communication Internal states
    const [comunicacaoInternaData, setComunicacaoInternaData] = useState(null);
    const [showViewCommunicationModal, setShowViewCommunicationModal] = useState(false);
    const [viewCommunicationId, setViewCommunicationId] = useState(null);

    // Add Message Modal state
    const [showAddMessageModal, setShowAddMessageModal] = useState(false);

    // Date Picker Modal states
    const [showDatePickerModal, setShowDatePickerModal] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [datePickerType, setDatePickerType] = useState('');
    const [datePickerTitle, setDatePickerTitle] = useState('');

    // Regional Modal states
    const [showRegionalModal, setShowRegionalModal] = useState(false);
    const [selectedRegional, setSelectedRegional] = useState(null);

    // Posto e Escala Modal states
    const [showPostoEscalaModal, setShowPostoEscalaModal] = useState(false);

    // Data e Hora Ausencia Modal states
    const [showDataHoraAusenciaModal, setShowDataHoraAusenciaModal] = useState(false);

    // Ausencia data state
    const [ausenciaData, setAusenciaData] = useState({});

    // ─── Shared refs ──────────────────────────────────────────────────────────

    const { profile } = useAuth();
    const history = useHistory();
    const { t: translate } = useTranslation();
    const mqttClientRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const statusTimeoutRef = useRef(null);
    const isMountedRef = useRef(true);
    const handleSelectOptionRef = useRef(null);
    const mudarStatusSessaoRef = useRef(null);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const goLink = (link) => history.push(Utils.setPrefix(link));

    // ─── API client ───────────────────────────────────────────────────────────

    const apiCHAT = useMemo(
        () =>
            Axios.create({
                baseURL:
                    process.env.NODE_ENV !== 'development'
                        ? process.env.REACT_APP_CHATBOT_API_URL_PRODUCTION
                        : process.env.REACT_APP_CHATBOT_API_URL_DEVELOPMENT,
            }),
        [],
    );

    const MQTT_URL = process.env.REACT_APP_MQTT_URL;

    // ─── Sub-hooks ────────────────────────────────────────────────────────────

    const sessions = useChatSessions({ apiCHAT, tokenChat, isMountedRef });

    const {
        chatSessions,
        setChatSessions,
        myStatusCount,
        generalStatusCount,
        totalPages,
        currentPage,
        searchQuery,
        setSearchQuery,
        searchStatus,
        setSearchStatus,
        statusTimeoutMapRef,
        fetchChatSessions,
        getElapsedTime,
        updateSessionInList,
        startTimeUpdates,
        stopTimeUpdates,
    } = sessions;

    const messageHandlers = useMessageHandlers({
        apiCHAT,
        tokenChat,
        profile,
        selectedSessionId,
        selectedSession,
        newMessage,
        setNewMessage,
        newTipoChat,
        setMessages,
        setOptions,
        setShowOptions,
        setShowInputBar,
        mqttClientRef,
        isMountedRef,
        markMessagesAsRead: (...args) => markMessagesAsRead(...args),
        comunicacaoInternaData,
        setComunicacaoInternaData,
        handleSelectOptionRef,
        setIsBotTyping,
        setLoadingPage,
        calcularSeguro,
    });

    const {
        broadcastSessionUpdate,
        broadcastBotMessage,
        handleDetectarIntencao,
        handleSendMessage,
        handleFileAttach,
        sendMessageStatus,
        sendTypingStatus,
        sendOptionAsMessage,
        handleComunicacaoInternaFileUpload,
    } = messageHandlers;

    const chatApi = useChatApi({
        apiCHAT,
        tokenChat,
        setTokenChat,
        profile,
        selectedSessionId,
        selectedSession,
        setSelectedSession,
        isMountedRef,
        broadcastSessionUpdate,
        updateSessionInList,
        fetchChatSessions,
        currentPage,
        searchQuery,
        searchStatus,
        setChatResponsavel,
        setChatProfile,
    });

    const {
        urlChat,
        gerarTokenChat,
        obterProfile,
        obterResponsavel,
        fetchAreasForCommunication,
        fetchTiposForCommunication,
        fetchCommunicationBySession,
        fetchRegionalCommunication,
        mudarStatusSessao,
        atribuirSessaoChat,
        handleLinkCommunication,
        lerMensagem,
    } = chatApi;

    const ausenciaApi = useAusenciaApi({ apiCHAT, tokenChat });

    const { fetchPessoaPostoLocalTrabalho, confirmarAusencia, fetchDetalhadoAusencia } = ausenciaApi;

    const onlineStatus = useOnlineStatus({ profile, selectedSession, isMountedRef });

    const {
        onlineUsers,
        setOnlineUsers,
        checkUserOnlineStatus,
        updateUsersOnlineStatus,
        getSessionOnlineStatus,
        startOnlineStatusUpdates,
        stopOnlineStatusUpdates,
    } = onlineStatus;

    const loanModal = useLoanModal({ apiCHAT, tokenChat, setMessages });

    const { showLoanModal, setShowLoanModal, loanData, loadingLoanData, fetchLoanData, openLoanModal } = loanModal;

    // Wrap handleLoanFormSubmit to pass UI setters
    const handleLoanFormSubmit = useCallback(
        async (formData) => {
            await loanModal.handleLoanFormSubmit(formData, {
                setOptions,
                setShowOptions,
                setShowInputBar,
                setNewTipoChat,
            });
        },
        [loanModal, setOptions, setShowOptions, setShowInputBar, setNewTipoChat],
    );

    // ─── Sync refs ────────────────────────────────────────────────────────────

    mudarStatusSessaoRef.current = mudarStatusSessao;

    // ─── Utils ────────────────────────────────────────────────────────────────

    const getStatusLabel = useCallback((statusKey) => {
        const statusObj = statusList.find((s) => s.key === statusKey);
        return statusObj ? statusObj.label : statusKey || 'Status não disponível';
    }, []);

    const getStatusStyle = (statusKey) => {
        const statusObj = statusList.find((item) => item && item.key === statusKey);
        const label = statusObj ? statusObj.label : 'SEM STATUS';
        let color = '#4caf50';
        let textColor = '#ffffff';

        switch (statusKey) {
            case 'PENDENTE':
            case 'PENDENTE_HUMANO':
            case 'AGUARDANDO_HUMANDO':
            case 'ATENDIMENTO_AUSENCIA_PENDENTE':
            case 'AUSENCIA_PENDENTE':
            case 'AUSENCIA_PROGRAMADA':
                color = '#ff9800';
                break;
            case 'NAO_RESOLVIDO':
            case 'AUSENCIA_URGENTE':
            case 'NAO_RESOLVIDO_HUMANO':
            case 'AUSENCIA_EMERGENCIAL':
                color = '#f44336';
                break;
            case 'CANCELADO':
            case 'AUSENCIA_CANCELADA':
            case 'ATENDIMENTO_AUSENCIA_CANCELADO':
                color = '#9e9e9e';
                break;
            case 'FINALIZADO':
            case 'FINALIZADO_HUMANO':
            case 'RESOLVIDO':
            case 'RESOLVIDO_HUMANO':
            case 'AUSENCIA_FINALIZADA':
            case 'ATENDIMENTO_AUSENCIA_CONCLUIDO':
            case 'AUSENCIA_EM_FINALIZACAO':
                color = '#06ee53ff';
                break;
            default:
                color = '#bdbdbd';
                textColor = 'rgba(0, 0, 0, 0.87)';
                break;
        }
        return { label, color, textColor };
    };

    const habilitarNovoChat = useCallback(() => {
        const activeStatuses = ['PENDENTE'];

        const hasActiveSessionsInCounts = activeStatuses.some((s) => (myStatusCount?.[s] ?? 0) > 0);
        const hasActiveSessionsInList = chatSessions.some(
            (session) => session.usuario.id === profile.pess_id_pessoa && activeStatuses.includes(session.status),
        );

        return !hasActiveSessionsInCounts && !hasActiveSessionsInList;
    }, [myStatusCount, chatSessions, profile.pess_id_pessoa]);

    // ─── Read status helpers ──────────────────────────────────────────────────

    const clearUnreadCount = useCallback(
        async (sessionId) => {
            if (!tokenChat || !isMountedRef.current || !sessionId) return;

            setChatSessions((prevSessions) =>
                prevSessions.map((session) => {
                    if (session.id === sessionId) {
                        return {
                            ...session,
                            last_message: { ...session.last_message, contar_nao_lida: 0 },
                        };
                    }
                    return session;
                }),
            );

            if (mqttClientRef.current?.connected) {
                mqttClientRef.current.publish(
                    'chatbot-web/',
                    JSON.stringify({
                        sessionId,
                        userId: profile.pess_id_pessoa,
                        userName: profile.pess_nome || 'Usuario',
                        type: 'unread_count_cleared',
                        timestamp: new Date().toISOString(),
                    }),
                );
            }
        },
        [tokenChat, profile.pess_id_pessoa, profile.pess_nome, setChatSessions],
    );

    const markMessagesAsRead = useCallback(
        async (sessionId) => {
            if (!tokenChat || !isMountedRef.current || !sessionId) return;

            try {
                setMessages((prevMessages) =>
                    prevMessages.map((message) => {
                        const isFromOtherUser =
                            message.remetente !== 'ISA' &&
                            message.remetente !== `${profile.pess_nome}-${profile.pess_id_pessoa}`;
                        const isUnread = !message.mensagem_lida || !message.mensagem_lida.lido;

                        if (isFromOtherUser && isUnread) {
                            return {
                                ...message,
                                mensagem_lida: {
                                    ...message.mensagem_lida,
                                    lido: true,
                                    data_leitura: new Date().toISOString(),
                                    minha_mensagem: false,
                                },
                            };
                        }
                        return message;
                    }),
                );

                await clearUnreadCount(sessionId);

                if (mqttClientRef.current?.connected) {
                    const readStatusUpdate = {
                        sessionId,
                        userId: profile.pess_id_pessoa,
                        userName: profile.pess_nome || 'Usuario',
                        type: 'messages_read',
                        timestamp: new Date().toISOString(),
                    };
                    mqttClientRef.current.publish(
                        `chat/session/${sessionId}/read-status`,
                        JSON.stringify(readStatusUpdate),
                    );
                    mqttClientRef.current.publish(
                        'chatbot-web/',
                        JSON.stringify({ ...readStatusUpdate, type: 'session_read_update' }),
                    );
                }
            } catch (error) {
                console.error('Erro ao marcar mensagens como lidas:', error);
            }
        },
        [tokenChat, profile.pess_id_pessoa, profile.pess_nome, clearUnreadCount, setMessages],
    );

    // ─── Date Picker Modal ────────────────────────────────────────────────────

    const openDatePickerModal = useCallback(async (type, title) => {
        setDatePickerType(type);
        setDatePickerTitle(title);
        setShowDatePickerModal(true);
        setShowOptions(false);
        setShowInputBar(false);
    }, []);

    const handleDateSelection = useCallback(
        async (date) => {
            if (!date) return;

            const nomeUsuario = `${profile.pess_nome}-${profile.pess_id_pessoa}`;
            const letraInicial = profile.pess_nome?.[0] ?? 'U';

            setSelectedDate(date);
            setShowDatePickerModal(false);

            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const formattedDate = `${day}/${month}/${year}`;

            const isEndDate = datePickerType === 'data_fim';
            const isAllStartDate = datePickerType === 'data_inicio_all';

            let confirmMessage = `Data de referência/Início selecionada: ${formattedDate}. Clique em "Data de referência/Início" para confirmar.`;
            let sendOptionKey = 'comunicacao_interna_interacao_atestados_data_inicio_atestados_enviar';

            if (isEndDate) {
                confirmMessage = `Data de fim selecionada: ${formattedDate}. Clique em "Enviar a Data Término" para confirmar.`;
                sendOptionKey = 'comunicacao_interna_interacao_atestados_data_fim_enviar';
            }
            if (isAllStartDate) {
                confirmMessage = `Data de referência/Início selecionada: ${formattedDate}. Clique em "Data de referência/Início" para confirmar.`;
                sendOptionKey = 'comunicacao_interna_interacao_data_inicio_enviar';
            }

            const userMsg = { remetente: nomeUsuario, texto: formattedDate, isOption: false, avatar: letraInicial };
            //setMessages((prev) => [...prev, userMsg, userMsg]);
            setMessages((prev) => [
                ...prev,
                { remetente: 'ISA', texto: confirmMessage, avatar: LogoImg, isOption: false },
            ]);

            const sendOption = [];
            if (!isEndDate && !isAllStartDate) {
                sendOption.push({ key: sendOptionKey, label: 'Enviar a Data de Referência/Início' });
                sendOption.push({
                    key: 'comunicacao_interna_interacao_atestados_data_inicio_atestados',
                    label: 'Alterar a Data de referência/Início',
                });
            }
            if (isEndDate) {
                sendOption.push({ key: sendOptionKey, label: 'Enviar a Data Término' });
                sendOption.push({
                    key: 'comunicacao_interna_interacao_atestados_data_fim',
                    label: 'Alterar a Data Término',
                });
            }
            if (isAllStartDate) {
                sendOption.push({ key: sendOptionKey, label: 'Enviar a Data de Referência/Início' });
                sendOption.push({
                    key: 'comunicacao_interna_interacao_data_inicio',
                    label: 'Alterar a Data de referência/Início',
                });
            }
            sendOption.push({ key: 'comunicacao_interna_interacao_area', label: 'Mudar o Área' });

            await sendOptionAsMessage(confirmMessage, 'isa_pergunta');

            setOptions(sendOption);
            setShowOptions(true);
            setShowInputBar(false);
        },
        [datePickerType, profile, setMessages, setOptions, setShowOptions, setShowInputBar, sendOptionAsMessage],
    );

    const closeDatePickerModal = useCallback(() => {
        setShowDatePickerModal(false);
        setShowOptions(true);
    }, []);

    // ─── Regional Modal ───────────────────────────────────────────────────────

    const openRegionalModal = useCallback(async () => {
        setShowRegionalModal(true);
        setShowOptions(false);
        setShowInputBar(false);
    }, []);

    const handleRegionalSelection = useCallback(async (optionKey, optionLabel) => {
        if (!optionKey || !optionLabel) return;
        setSelectedRegional({ key: optionKey, label: optionLabel });
        setShowRegionalModal(false);
        if (handleSelectOptionRef.current) {
            await handleSelectOptionRef.current(optionKey, optionLabel);
        }
    }, []);

    const closeRegionalModal = useCallback(() => setShowRegionalModal(false), []);

    // ─── Posto e Escala Modal ─────────────────────────────────────────────────
    const openPostoEscalaModal = useCallback(() => {
        setShowPostoEscalaModal(true);
        setShowOptions(false);
        setShowInputBar(false);
    }, []);

    const closePostoEscalaModal = useCallback(() => {
        setShowPostoEscalaModal(false);
        setShowOptions(true);
    }, []);

    // ─── Data e Hora Ausencia Modal ───────────────────────────────────────────
    const openDataHoraAusenciaModal = useCallback(() => {
        setShowDataHoraAusenciaModal(true);
        setShowOptions(false);
        setShowInputBar(false);
    }, []);

    const closeDataHoraAusenciaModal = useCallback(() => {
        setShowDataHoraAusenciaModal(false);
        setShowOptions(true);
    }, []);

    // ─── Session messages & selection ─────────────────────────────────────────

    const setupSessionInterface = useSetupSessionInterface({
        setMessages,
        setOptions,
        setShowOptions,
        setShowInputBar,
        setNewTipoChat,
        setLastTypeChat,
        setLastAreaChat,
        profile,
        chatProfile,
        isMountedRef,
        selectedSessionId,
        defaultOptions,
        defaultOptionsNew,
        ausenciaDefault,
        fetchAreasForCommunication,
        fetchTiposForCommunication,
        fetchRegionalCommunication,
        fetchCommunicationBySession,
        mudarStatusSessao: (...args) => mudarStatusSessaoRef.current?.(...args),
        handleSelectOption: (...args) => handleSelectOptionRef.current?.(...args),
        setLoadingPage,
    });

    const loadSessionMessages = useCallback(
        async (sessionId) => {
            if (!tokenChat || !isMountedRef.current) return;

            const responsavel = await obterResponsavel(sessionId, tokenChat);

            try {
                const response = await apiCHAT.get('chat_mensagens', {
                    params: { id_sessao_chat: sessionId, id_usuario: profile.pess_id_pessoa },
                    headers: { Authorization: `Bearer ${tokenChat}` },
                });

                const responseData = response.data;
                let messagesData = [];

                if (responseData?.data && Array.isArray(responseData.data)) {
                    messagesData = responseData.data;
                    setComunicacaoInternaData(responseData.chatbot_sessao_comunicacao_interna_altum || null);
                } else if (Array.isArray(responseData)) {
                    messagesData = responseData;
                    setComunicacaoInternaData(null);
                } else {
                    messagesData = [];
                    setComunicacaoInternaData(null);
                }

                if (isMountedRef.current) {
                    setMessages(messagesData);
                    const session = chatSessions.find((s) => s.id === sessionId);
                    if (session) await setupSessionInterface(session, responsavel);
                }
            } catch (error) {
                console.error('Erro ao carregar mensagens:', error);
            }
        },
        [
            apiCHAT,
            tokenChat,
            profile.pess_id_pessoa,
            chatSessions,
            obterResponsavel,
            setupSessionInterface,
            setComunicacaoInternaData,
            setMessages,
        ],
    );

    const selectSession = useCallback(
        async (session) => {
            setMessages([]);
            setOptions(defaultOptions);
            setShowOptions(false);
            setShowInputBar(false);
            setNewTipoChat('');
            setLastTypeChat(false);
            setLastAreaChat(false);
            setSelectedSessionId(session.id);
            setSelectedSession(session);
            await loadSessionMessages(session.id);
            await markMessagesAsRead(session.id);
        },
        [loadSessionMessages, markMessagesAsRead],
    );

    // ─── New chat ─────────────────────────────────────────────────────────────

    const handleNewChat = useCallback(async () => {
        if (!tokenChat) return;
        setMessages([]);
        setStatus('');
        setOptions(defaultOptions);
        setNewTipoChat('');
        setLastTypeChat(false);
        setLastAreaChat(false);

        try {
            const response = await apiCHAT.post(
                urlChat('start_chat'),
                { status: 'PENDENTE', id_cliente: profile.pess_id_cliente_principal },
                { headers: { Authorization: `Bearer ${tokenChat}` } },
            );

            await fetchChatSessions(1);

            const newSessionId = response.data.session_id;
            const newSession = {
                id: newSessionId,
                status: 'PENDENTE',
                usuario: { nome: profile.pess_nome, id: profile.pess_id_pessoa },
                data_criacao: new Date().toISOString(),
            };

            //defaultOptionsNew

            setSelectedSessionId(newSessionId);
            setSelectedSession(newSession);
            setMessages([
                {
                    remetente: 'ISA',
                    texto: 'Olá, seja bem-vindo(a) ao chat do Grupo Altum! Para continuar, selecione uma das opções abaixo ou digite claramente o que você precisa:',
                    avatar: LogoImg,
                    isOption: false,
                },
            ]);

            setOptions(defaultOptionsNew);
            setShowOptions(true);
            setShowInputBar(true);
        } catch (error) {
            console.error('Erro ao criar novo chat:', error);
        }
    }, [apiCHAT, tokenChat, urlChat, profile, fetchChatSessions, setMessages, setStatus]);

    // ─── Initial data load ────────────────────────────────────────────────────

    const initHasRunRef = useRef(false);
    useEffect(() => {
        if (initHasRunRef.current) return;
        initHasRunRef.current = true;

        const init = async () => {
            let token = tokenChat;
            if (!token) {
                token = await gerarTokenChat();
                if (!token) return;
            }
            await fetchChatSessions(1, token);
            await obterProfile();
        };

        init();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Visibility change (presence) ─────────────────────────────────────────

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (mqttClientRef.current?.connected && profile?.pess_id_pessoa) {
                mqttClientRef.current.publish(
                    'chatbot-web/presence',
                    JSON.stringify({
                        userId: profile.pess_id_pessoa,
                        online: !document.hidden,
                        timestamp: new Date().toISOString(),
                        userName: profile.pess_nome || 'Usuario',
                    }),
                );
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [profile, selectedSessionId, markMessagesAsRead]);

    // ─── MQTT setup ───────────────────────────────────────────────────────────

    useEffect(() => {
        if (!MQTT_URL) return;

        const client = mqtt.connect(MQTT_URL);
        mqttClientRef.current = client;

        client.on('connect', () => {
            console.log('MQTT conectado');
            client.subscribe(['chatbot-web/', 'chatbot-web/presence'], (err) => {
                if (err) console.error('Erro ao inscrever no dashboard:', err);
                else console.log('Inscrito no tópico dashboard e presence');
            });

            if (profile?.pess_id_pessoa) {
                client.publish(
                    'chatbot-web/presence',
                    JSON.stringify({
                        userId: profile.pess_id_pessoa,
                        online: true,
                        timestamp: new Date().toISOString(),
                        userName: profile.pess_nome || 'Usuario',
                    }),
                );
            }

            if (selectedSessionId) {
                const topics = [
                    `chat/session/${selectedSessionId}`,
                    `chat/session/${selectedSessionId}/typing`,
                    `chat/session/${selectedSessionId}/status`,
                    `chat/session/${selectedSessionId}/read-status`,
                ];
                client.subscribe(topics, (err) => {
                    if (err) console.error('Erro ao inscrever nos tópicos da sessão:', err);
                });
            }
        });

        client.on('message', (receivedTopic, message) => {
            if (!isMountedRef.current) return;

            try {
                const data = JSON.parse(message.toString());

                // Presence updates
                if (receivedTopic === 'chatbot-web/presence') {
                    if (data.userId && typeof data.online === 'boolean') {
                        setOnlineUsers((prev) => {
                            const updated = new Map(prev);
                            updated.set(data.userId, data.online);
                            return updated;
                        });
                    }
                    return;
                }

                // Dashboard messages
                if (receivedTopic === 'chatbot-web/') {
                    if (!isMountedRef.current) return;

                    if (
                        (data.userId === profile.pess_id_pessoa && data.type) === 'unread_count_cleared' &&
                        data.sessionId
                    ) {
                        setChatSessions((prevSessions) =>
                            prevSessions.map((session) => {
                                if (session.id === data.sessionId) {
                                    return {
                                        ...session,
                                        last_message: { ...session.last_message, contar_nao_lida: 0 },
                                    };
                                }
                                return session;
                            }),
                        );
                        return;
                    }

                    if (
                        data.type === 'new_message_received' &&
                        data.sessionId &&
                        data.userId !== profile.pess_id_pessoa &&
                        data.userId !== undefined
                    ) {
                        setChatSessions((prevSessions) =>
                            prevSessions.map((session) => {
                                if (session.id === data.sessionId) {
                                    const currentUnreadCount = session.last_message?.contar_nao_lida || 0;
                                    return {
                                        ...session,
                                        last_message: {
                                            ...session.last_message,
                                            mensagem_usuario:
                                                data.messageText || session.last_message?.mensagem_usuario,
                                            data_mensagem: data.timestamp || new Date().toISOString(),
                                            contar_nao_lida: currentUnreadCount + 1,
                                        },
                                    };
                                }
                                return session;
                            }),
                        );
                        return;
                    }

                    if (data.sessionId && data.status) {
                        if (statusTimeoutMapRef.current.has(data.sessionId)) {
                            clearTimeout(statusTimeoutMapRef.current.get(data.sessionId));
                            statusTimeoutMapRef.current.delete(data.sessionId);
                        }

                        updateSessionInList(data.sessionId, data?.status || 'PENDENTE', data);

                        if (selectedSession?.id === data.sessionId) {
                            setSelectedSession((prev) => ({ ...prev, status: data.status }));
                        }

                        if (data.temporaryStatus && data.revertAfter) {
                            const timeoutId = setTimeout(() => {
                                if (isMountedRef.current)
                                    updateSessionInList(data.sessionId, data.originalStatus || 'PENDENTE', data);
                            }, data.revertAfter);
                            statusTimeoutMapRef.current.set(data.sessionId, timeoutId);
                        }
                    }

                    if (data.status && data.userId !== profile.pess_id_pessoa) {
                        setStatus(`Sessão ${data.sessionId}: ${getStatusLabel(data.status)}`);
                        clearTimeout(statusTimeoutRef.current);
                        statusTimeoutRef.current = setTimeout(() => {
                            if (isMountedRef.current) setStatus('');
                        }, 3000);
                    }
                }

                // Session-specific messages
                if (selectedSessionId) {
                    if (receivedTopic === `chat/session/${selectedSessionId}/typing`) {
                        if (!isMountedRef.current) return;
                        if (data.userId !== profile.pess_id_pessoa) {
                            setRemoteTypingStatus(data.isTyping);
                            clearTimeout(typingTimeoutRef.current);

                            if (data.userId && data.isTyping) {
                                setOnlineUsers((prev) => {
                                    const u = new Map(prev);
                                    u.set(data.userId, true);
                                    return u;
                                });
                            }

                            if (data.isTyping) {
                                typingTimeoutRef.current = setTimeout(() => {
                                    if (isMountedRef.current) setRemoteTypingStatus(false);
                                }, 3000);
                            }
                        }
                    }

                    if (receivedTopic === `chat/session/${selectedSessionId}/read-status`) {
                        if (!isMountedRef.current) return;
                        if (data.userId !== profile.pess_id_pessoa && data.type === 'messages_read') {
                            setMessages((prevMessages) =>
                                prevMessages.map((message) => {
                                    const isMyMessage =
                                        message.remetente === `${profile.pess_nome}-${profile.pess_id_pessoa}`;
                                    if (isMyMessage) {
                                        return {
                                            ...message,
                                            mensagem_lida: {
                                                ...message.mensagem_lida,
                                                lido: true,
                                                data_leitura: data.timestamp,
                                                minha_mensagem: true,
                                            },
                                        };
                                    }
                                    return message;
                                }),
                            );
                        }
                    }

                    if (receivedTopic === `chat/session/${selectedSessionId}/status`) {
                        if (!isMountedRef.current) return;
                        if (data.userId !== profile.pess_id_pessoa) {
                            if (data && (data.texto || data.remetente)) setMessages((prev) => [...prev, data]);
                        }
                    }

                    if (receivedTopic === `chat/session/${selectedSessionId}`) {
                        if (!isMountedRef.current) return;
                        if (data.userId !== profile.pess_id_pessoa) {
                            setRemoteTypingStatus(false);
                            if (data.userId)
                                setOnlineUsers((prev) => {
                                    const u = new Map(prev);
                                    u.set(data.userId, true);
                                    return u;
                                });
                        }
                        if (data && (data.texto || data.remetente)) {
                            setMessages((prev) => [...prev, data]);

                            if (data.userId !== profile.pess_id_pessoa && data.sessionId) {
                                lerMensagem(data.message_id, data.sessionId);
                                updateSessionInList(data.sessionId, selectedSession?.status || 'PENDENTE', data);
                            } else {
                                updateSessionInList(data.sessionId, data?.status || 'PENDENTE', data);
                            }
                        }
                    }
                }
            } catch (error) {
                console.error('Error parsing MQTT message:', error);
            }
        });

        return () => {
            if (client) {
                if (profile?.pess_id_pessoa && client.connected) {
                    client.publish(
                        'chatbot-web/presence',
                        JSON.stringify({
                            userId: profile.pess_id_pessoa,
                            online: false,
                            timestamp: new Date().toISOString(),
                            userName: profile.pess_nome || 'Usuario',
                        }),
                    );
                }
                client.removeAllListeners('connect');
                client.removeAllListeners('message');
                client.removeAllListeners('error');
                client.removeAllListeners('disconnect');
                if (client.connected) client.end(true);
                mqttClientRef.current = null;
            }
            clearTimeout(typingTimeoutRef.current);
            clearTimeout(statusTimeoutRef.current);
            statusTimeoutMapRef.current.forEach((id) => clearTimeout(id));
            statusTimeoutMapRef.current.clear();
        };
    }, [selectedSessionId, profile.pess_id_pessoa, selectedSession, updateSessionInList]);

    // ─── Use HandleSelectOption ───────────────────────────────────────────────

    const handleSelectOption = useHandleSelectOption({
        setMessages,
        setOptions,
        setShowOptions,
        setShowInputBar,
        setNewTipoChat,
        setLastTypeChat,
        setLastAreaChat,
        setSelectedSessionId,
        setSelectedSession,
        setComunicacaoInternaData,
        selectedSessionId,
        profile,
        newTipoChat,
        lastTypeChat,
        lastAreaChat,
        comunicacaoInternaData,
        calcularSeguro,
        setCalcularSeguro,
        selectedDate,
        defaultOptions,
        defaultOptionsNew,
        optionsSubInformacoesGerais,
        optionsManuaisTutoriais,
        finalizar,
        finalizarIsa,
        atendimentoOptions,
        finalizarAtendimento,
        optionsEmprestimo,
        ausenciaDefault,
        optionsComunicacaoInternaAreaAtestados,
        optionsComunicacaoInternaAreaDocumentos,
        optionsComunicacaoInternaAreaValeAlimentacao,
        optionsComunicacaoInternaAreaValeTransporte,
        optionsComunicacaoInternaAreaVR,
        optionsComunicacaoInternaAreaVRAuto,
        mudarStatusSessao,
        atribuirSessaoChat,
        sendMessageStatus,
        sendOptionAsMessage,
        openLoanModal,
        handleDetectarIntencao,
        openAddMessageModal: () => setShowAddMessageModal(true),
        openViewCommunicationModal: (id) => {
            setViewCommunicationId(id);
            setShowViewCommunicationModal(true);
        },
        setLoadingPage,
        openDatePickerModal,
        openRegionalModal,
        fetchAreasForCommunication,
        fetchTiposForCommunication,
        fetchRegionalCommunication,
        fetchCommunicationBySession,
        handleComunicacaoInternaFileUpload,
        fetchPessoaPostoLocalTrabalho,
        confirmarAusencia,
        fetchDetalhadoAusencia,
        openPostoEscalaModal,
        openDataHoraAusenciaModal,
        setAusenciaData,
        ausenciaData,
    });

    handleSelectOptionRef.current = handleSelectOption;

    // ─── API interceptor ──────────────────────────────────────────────────────

    apiCHAT.interceptors.response.use(
        (response) => response,
        (error) => {
            if (error.response?.status === 401) {
                Store.remove('token_chat');
                Store.remove('id_session_chat');
                setTokenChat(null);
            }
            return Promise.reject(error);
        },
    );

    // ─── Return ───────────────────────────────────────────────────────────────

    return {
        // Session list
        chatSessions,
        currentPage,
        totalPages,
        myStatusCount,
        generalStatusCount,
        searchQuery,
        setSearchQuery,
        searchStatus,
        setSearchStatus,
        fetchChatSessions,

        // Chat state
        selectedSessionId,
        selectedSession,
        messages,
        newMessage,
        setNewMessage,
        showOptions,
        options,
        showInputBar,
        remoteTypingStatus,
        isBotTyping,
        setOptions,
        setShowOptions,
        setShowInputBar,
        setMessages,
        lastTypeChat,
        lastAreaChat,
        setLastTypeChat,
        setLastAreaChat,

        // Actions
        selectSession,
        handleSendMessage,
        handleFileAttach,
        handleSelectOption,
        handleNewChat,
        setSelectedSessionId,
        setSelectedSession,
        sendTypingStatus,
        markMessagesAsRead,
        clearUnreadCount,

        // Loan modal
        showLoanModal,
        setShowLoanModal,
        loanData,
        loadingLoanData,
        loadingPage,
        fetchLoanData,
        handleLoanFormSubmit,

        // Add Message Modal
        showAddMessageModal,
        setShowAddMessageModal,
        mudarStatusSessao,
        handleLinkCommunication,
        fetchRegionalCommunication,

        // View Communication Modal
        showViewCommunicationModal,
        setShowViewCommunicationModal,
        viewCommunicationId,

        // Date Picker Modal
        showDatePickerModal,
        setShowDatePickerModal,
        selectedDate,
        setSelectedDate,
        datePickerTitle,
        handleDateSelection,
        closeDatePickerModal,

        // Regional Modal
        showRegionalModal,
        setShowRegionalModal,
        selectedRegional,
        handleRegionalSelection,
        closeRegionalModal,
        openRegionalModal,

        // Posto e Escala Modal
        showPostoEscalaModal,
        openPostoEscalaModal,
        closePostoEscalaModal,

        // Data e Hora Ausencia Modal
        showDataHoraAusenciaModal,
        openDataHoraAusenciaModal,
        closeDataHoraAusenciaModal,

        // Ausencia Data
        ausenciaData,
        setAusenciaData,

        // Utils
        statusList,
        getStatusStyle,
        habilitarNovoChat,
        chatProfile,
        profile,
        transitionIn,
        goLink,
        tokenChat,
        LogoImg,
        LogoImgSad,
        newTipoChat,
        setNewTipoChat,
        lerMensagem,

        // MQTT / real-time
        getElapsedTime,
        updateSessionInList,
        broadcastSessionUpdate,
        getStatusLabel,
        startTimeUpdates,
        stopTimeUpdates,

        // Online status
        checkUserOnlineStatus,
        getSessionOnlineStatus,
        updateUsersOnlineStatus,
        startOnlineStatusUpdates,
        stopOnlineStatusUpdates,

        // Options arrays (passed through for consumers)
        optionsComunicacaoInternaAreaAtestados,
        optionsComunicacaoInternaAreaDocumentos,
        optionsComunicacaoInternaAreaValeAlimentacao,
        optionsComunicacaoInternaAreaValeTransporte,
        optionsComunicacaoInternaAreaVR,
        optionsComunicacaoInternaAreaVRAuto,

        // Communication
        fetchCommunicationBySession,
        handleComunicacaoInternaFileUpload,
        comunicacaoInternaData,
    };
};

export default useChatbotWeb;
